import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Space,
  Switch,
  Divider,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { dateHelper } from "@iso/lib/helpers/dateHelper";
import { serviceLogService } from "@iso/services";
import FileUploader from "@iso/components/FileUploader/FileUploader";
import styled from "styled-components";

const { Option } = Select;
const { TextArea } = Input;

// Two-column grid for the per-part photo thumbnails (matches the inspection
// modal). Scoped so other FileUploaders keep their default single-column list.
const PhotoGrid = styled.div`
  .ant-upload-list-picture {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
  }
  /* AntD's clearfix ::before/::after otherwise become stray grid cells (leaving
     an empty top-left cell) — remove them so thumbnails fill from the top-left. */
  .ant-upload-list-picture::before,
  .ant-upload-list-picture::after {
    display: none;
  }
  .ant-upload-list-picture .ant-upload-list-item {
    margin-top: 0;
  }
`;

const SERVICE_TYPES = [
  { value: "scheduled_service", label: "Scheduled Service" },
  { value: "breakdown_repair", label: "Breakdown Repair" },
  { value: "inspection", label: "Inspection" },
  { value: "compliance_service", label: "Compliance Service" },
];

const ACTIONS = [
  { value: "inspect", label: "Inspect" },
  { value: "replace", label: "Replace" },
  { value: "clean", label: "Clean" },
  { value: "lube", label: "Lube" },
  { value: "adjust", label: "Adjust" },
];

const dateFormat = "DD/MM/YYYY";

// Log a service against a machinery asset. Self-contained: takes the asset
// and reports back via onSaved so the parent can refresh (current_reading
// advances on the backend).
const LogServiceModal = (props) => {
  const { visible, asset, onCancel, onSaved } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector((state) => state.user.user);
  const [itemLibrary, setItemLibrary] = useState([]);
  const [existingParts, setExistingParts] = useState([]);

  const propertyId = (asset && asset.property_id) || null;

  // Load the reusable service-item library + existing-parts catalogue for the
  // asset's property when the modal opens.
  useEffect(() => {
    if (!visible || !propertyId) return;
    serviceLogService
      .getItemLibrary(propertyId)
      .then((res) => setItemLibrary((res && res.items) || []))
      .catch((err) => console.error("Failed to load item library:", err));
    serviceLogService
      .getExistingParts(propertyId)
      .then((res) => setExistingParts((res && res.parts) || []))
      .catch((err) => console.error("Failed to load existing parts:", err));
  }, [visible, propertyId]);

  const filterContains = (input, option) =>
    (option?.value || "").toLowerCase().includes((input || "").toLowerCase());

  // Live total preview (labour + parts) — backend recomputes authoritatively.
  const values = Form.useWatch([], form);
  const computeTotal = () => {
    const v = form.getFieldsValue();
    const labour = (Number(v.labour_hours) || 0) * (Number(v.labour_rate) || 0);
    let parts = 0;
    (v.items || []).forEach((item) => {
      (item?.parts || []).forEach((p) => {
        parts += (Number(p?.quantity) || 0) * (Number(p?.unit_cost) || 0);
      });
    });
    return labour + parts;
  };

  const handleOk = () => {
    setLoading(true);
    form
      .validateFields()
      .then(() => {
        // getFieldsValue(true) returns ALL fields incl. per-part image_ids set
        // imperatively by the photo uploader (which aren't registered Form.Items).
        const vals = form.getFieldsValue(true);
        const signOff = vals.sign_off;
        const payload = {
          service_log: {
            facility_id: asset.id,
            property_id: asset.property_id,
            service_date: vals.service_date ? dateHelper.dateForAPI(vals.service_date) : "",
            reading: vals.reading ?? null,
            reading_unit: vals.reading_unit || asset.reading_unit || null,
            service_type: vals.service_type || null,
            labour_hours: vals.labour_hours ?? null,
            labour_rate: vals.labour_rate ?? null,
            // Blank => backend auto-totals (labour + parts); a value here overrides it.
            total_cost: vals.total_cost ?? null,
            notes: vals.notes || null,
            signed_off_by: signOff && currentUser ? currentUser.id : null,
            signed_off_at: signOff ? moment().format("YYYY-MM-DD HH:mm:ss") : null,
            items: (vals.items || []).map((item) => ({
              action: item.action,
              description: item.description || null,
              parts: (item.parts || []).map((p) => ({
                part_name: p.part_name || null,
                part_number: p.part_number || null,
                quantity: p.quantity ?? null,
                unit_cost: p.unit_cost ?? null,
                image_ids: p.image_ids || [],
              })),
            })),
          },
        };

        return serviceLogService.storeOrUpdate(payload).then((res) => {
          if (res && res.code === "0000") {
            message.success("Service logged");
            form.resetFields();
            if (onSaved) onSaved(res.service_log);
          } else {
            message.error((res && res.message) || "Failed to log service");
          }
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancel) onCancel();
  };

  return (
    <Modal
      open={visible}
      title={`Log Service${asset?.title ? ` — ${asset.title}` : ""}`}
      onCancel={handleCancel}
      maskClosable={false}
      width={760}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
          Save
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          service_date: moment(),
          reading: asset?.current_reading ?? undefined,
          reading_unit: asset?.reading_unit ?? undefined,
          service_type: "scheduled_service",
          items: [{ action: "inspect", parts: [] }],
        }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="service_date"
              label="Service Date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker style={{ width: "100%" }} format={dateFormat} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="reading" label={`Reading${asset?.reading_unit ? ` (${asset.reading_unit})` : ""}`}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 1500" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="service_type" label="Type of Service">
              <Select>
                {SERVICE_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Service Items</Divider>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              {fields.map((field) => (
                <Card
                  key={field.key}
                  size="small"
                  title={`Item ${field.name + 1}`}
                  extra={
                    <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  }
                >
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name={[field.name, "action"]}
                        label="Action"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Select placeholder="Select action">
                          {ACTIONS.map((a) => (
                            <Option key={a.value} value={a.value}>
                              {a.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item name={[field.name, "description"]} label="Description">
                        <AutoComplete
                          options={itemLibrary.map((li) => ({
                            value: li.name,
                            label: li.name,
                            action: li.default_action,
                          }))}
                          filterOption={filterContains}
                          onSelect={(val, option) => {
                            if (option && option.action) {
                              form.setFields([
                                { name: ["items", field.name, "action"], value: option.action },
                              ]);
                            }
                          }}
                          placeholder="e.g. Replace engine oil filter (or pick from library)"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.List name={[field.name, "parts"]}>
                    {(partFields, { add: addPart, remove: removePart }) => (
                      <div style={{ paddingLeft: 8 }}>
                        {partFields.map((pf) => (
                          <div key={pf.key} style={{ borderBottom: "1px dashed #eee", marginBottom: 8, paddingBottom: 4 }}>
                            <Row gutter={8} align="middle">
                              <Col xs={24} md={7}>
                                <Form.Item name={[pf.name, "part_name"]} label="Part">
                                  <AutoComplete
                                    options={existingParts.map((ep) => ({
                                      value: ep.part_name,
                                      label: ep.part_number ? `${ep.part_name} (${ep.part_number})` : ep.part_name,
                                      part_number: ep.part_number,
                                      unit_cost: ep.unit_cost,
                                    }))}
                                    filterOption={filterContains}
                                    onSelect={(val, option) => {
                                      const base = ["items", field.name, "parts", pf.name];
                                      const fields = [];
                                      if (option && option.part_number != null && option.part_number !== "") {
                                        fields.push({ name: [...base, "part_number"], value: option.part_number });
                                      }
                                      if (option && option.unit_cost != null && option.unit_cost !== "") {
                                        fields.push({ name: [...base, "unit_cost"], value: Number(option.unit_cost) });
                                      }
                                      if (fields.length) form.setFields(fields);
                                    }}
                                    placeholder="Part name"
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={5}>
                                <Form.Item name={[pf.name, "part_number"]} label="Part #">
                                  <Input placeholder="Part number" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={4}>
                                <Form.Item name={[pf.name, "quantity"]} label="Qty">
                                  <InputNumber min={0} style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={5}>
                                <Form.Item name={[pf.name, "unit_cost"]} label="Unit cost">
                                  <InputNumber min={0} style={{ width: "100%" }} prefix="$" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={3}>
                                <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removePart(pf.name)} />
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={24}>
                                <PhotoGrid>
                                <FileUploader
                                  name="Image[file]"
                                  type="service_log_part"
                                  multiple
                                  isCompressFile
                                  listType="picture"
                                  handleOnSuccess={(image) => {
                                    if (!image || !image.id) return;
                                    const path = ["items", field.name, "parts", pf.name, "image_ids"];
                                    const cur = form.getFieldValue(path) || [];
                                    if (!cur.includes(image.id)) {
                                      form.setFields([{ name: path, value: [...cur, image.id] }]);
                                    }
                                  }}
                                  handleOnRemove={(file) => {
                                    const removedId = file && file.response && file.response.image && file.response.image.id;
                                    if (!removedId) return;
                                    const path = ["items", field.name, "parts", pf.name, "image_ids"];
                                    const cur = form.getFieldValue(path) || [];
                                    form.setFields([{ name: path, value: cur.filter((id) => id !== removedId) }]);
                                  }}
                                />
                                </PhotoGrid>
                              </Col>
                            </Row>
                          </div>
                        ))}
                        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addPart({})}>
                          Add part
                        </Button>
                      </div>
                    )}
                  </Form.List>
                </Card>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ action: "inspect", parts: [] })}>
                Add service item
              </Button>
            </Space>
          )}
        </Form.List>

        <Divider orientation="left">Labour & Sign-off</Divider>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="labour_hours" label="Labour hours">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="labour_rate" label="Labour rate ($/hr)">
              <InputNumber min={0} style={{ width: "100%" }} prefix="$" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="total_cost"
              label="Total cost"
              tooltip="Auto-calculated from labour + parts. Leave blank to use the auto-total, or enter a value to override it."
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                prefix="$"
                placeholder={`Auto: ${computeTotal().toFixed(2)}`}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={3} placeholder="Additional notes" />
        </Form.Item>
        <Form.Item name="sign_off" label="Sign off" valuePropName="checked" tooltip="Record you as the person signing off this service.">
          <Switch checkedChildren="Signed" unCheckedChildren="No" />
        </Form.Item>
        {values?.sign_off && currentUser && (
          <p style={{ color: "rgba(0,0,0,0.45)" }}>
            Signed off by {currentUser.name} {currentUser.surname} on {moment().format("ll")}
          </p>
        )}
      </Form>
    </Modal>
  );
};

export default LogServiceModal;
